import { Modal, ModalProps, Spin, Input } from 'antd';
import { cloneDeep } from 'lodash';
import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { SearchOutlined } from '@ant-design/icons';
import { EQueryIdType } from '@src/emp-3.0/types';
import { EXPLORER_DIMENSION_KEYS } from '@src/emp-3.0/utils/constants';
import { TableWrapper } from '@src/pages/livelens/styled';
import { getTimeDifferenceDescription } from '@utils/humanize';

interface IFindUserByIpModal {
  open: ModalProps['open'];
  data: any;
  isLoading: boolean;
  onClickId: (record: any) => void;
  onCancel: ModalProps['onCancel'];
  afterClose: ModalProps['afterClose'];
  titleValue: EQueryIdType;
  isPending: boolean;
  onIsPendingChange: (isPending: boolean) => void;
}

const TitleRender = styled.div`
  width: 100;
`;
const PauseButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  cursor: pointer;
  color: #0046ff;
  border: #b3c8ff 1px solid;
  border-radius: 2px;
  height: 28px;
  width: 70px;
  flex-shrink: 0;
  margin-right: 10px;
  svg {
    color: #0046ff;
    display: inline-block;
    margin-right: 6px;
  }
  &:hover {
    background: #b3c8ff;
  }
`;

const StartButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  cursor: pointer;
  color: #52c41a;
  border: #52c41a 1px solid;
  border-radius: 2px;
  height: 28px;
  width: 70px;
  flex-shrink: 0;
  margin-right: 10px;
  svg {
    color: #52c41a;
    display: inline-block;
    margin-right: 6px;
  }
  &:hover {
    background: #d9f7be;
  }
`;
const ModelHeader = styled.div`
  margin-bottom: 12px;
  margin-right: 24px;
  > div:first-child {
    display: flex;
    align-items: flex-start;
    margin-right: 24px;
    > span:nth-child(2) {
      flex: 1;
    }
  }
`;

const PauseButton = () => (
  <PauseButtonWrapper>
    <svg width="14" height="20" viewBox="0 0 14 20" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4.375 4.75H5.25V15.25H4.375V4.75ZM9.625 4.75V15.25H8.75V4.75H9.625Z"
        fill="#0046FF"
      />
    </svg>
    <span>Pause</span>
  </PauseButtonWrapper>
);

const StartButton = () => (
  <StartButtonWrapper>
    <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7.5 6L0 11.25V0.75L7.5 6ZM0.75 9.80859L6.19336 6L0.75 2.19141V9.80859Z"
        fill="#52C41A"
      />
    </svg>
    <span>Start</span>
  </StartButtonWrapper>
);

const FindUserByIpModal: React.FC<IFindUserByIpModal> = ({
  open,
  data,
  isLoading,
  onClickId,
  onCancel,
  afterClose,
  titleValue,
  isPending,
  onIsPendingChange,
}) => {
  // const [openDropdown, setOpenDropdown] = useState<boolean>(false);

  const [searchText, setSearchText] = useState<string>('');

  const columns = [
    {
      title: '#',
      key: 'row',
      render: (text, record, index) => {
        return `${index + 1}`;
      },
    },
    {
      title: 'Last Seen',
      dataIndex: EXPLORER_DIMENSION_KEYS.MAX_EVENT_TIME,
      key: EXPLORER_DIMENSION_KEYS.MAX_EVENT_TIME,
      width: 180,
      render: (text) => {
        return (
          <span style={{ display: 'inline-block', width: '200px' }}>
            {moment(text).format('HH:mm:ss')} ({getTimeDifferenceDescription(moment(text))})
          </span>
        );
      },
    },
    {
      title: 'Client ID',
      dataIndex: EXPLORER_DIMENSION_KEYS.CLIENT_ID,
      key: EXPLORER_DIMENSION_KEYS.CLIENT_ID,
      render: (text, record) => {
        return (
          <span
            className="cell-link"
            onClick={() => {
              onClickId(record);
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: 'User ID',
      dataIndex: EXPLORER_DIMENSION_KEYS.USER_ID,
      key: EXPLORER_DIMENSION_KEYS.USER_ID,
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_v4',
      key: 'ip_v4',
      render: (text, record) => {
        return (
          <span>
            {text || ''}
            {record.ip_v6}
          </span>
        );
      },
    },
    {
      title: <TitleRender>App Name</TitleRender>,
      dataIndex: EXPLORER_DIMENSION_KEYS.APP_ID,
      key: EXPLORER_DIMENSION_KEYS.APP_ID,
    },
    {
      title: <TitleRender>App Version</TitleRender>,
      dataIndex: EXPLORER_DIMENSION_KEYS.APPLICATION_VERSION,
      key: EXPLORER_DIMENSION_KEYS.APPLICATION_VERSION,
    },
    {
      title: <TitleRender>Device Name</TitleRender>,
      dataIndex: EXPLORER_DIMENSION_KEYS.DEVICE_NAME,
      key: EXPLORER_DIMENSION_KEYS.DEVICE_NAME,
    },
    {
      title: <TitleRender>Device Model</TitleRender>,
      dataIndex: EXPLORER_DIMENSION_KEYS.DA_MODEL,
      key: EXPLORER_DIMENSION_KEYS.DA_MODEL,
    },
    {
      title: <div style={{ minWidth: 120 }}>Browser Name</div>,
      dataIndex: EXPLORER_DIMENSION_KEYS.BROWSER_NAME,
      key: EXPLORER_DIMENSION_KEYS.BROWSER_NAME,
    },
  ];
  const searchTypeText =
    titleValue === EQueryIdType.ip
      ? 'Connect your device to the IP above and interact with your APP/Website.'
      : EQueryIdType.userId
      ? 'Interact with your APP/Website with the User ID above.'
      : 'Interact with your APP/Website with the Client ID above.';

  const onSearch = (e) => {
    setSearchText(e.target.value);
  };

  const resData = useMemo(() => {
    // search
    if (searchText) {
      const lowerCaseST = searchText.toLowerCase();
      return data.filter((item) => {
        const cpItem = cloneDeep(item);
        delete cpItem[EXPLORER_DIMENSION_KEYS.MAX_EVENT_TIME];
        const values = Object.values(cpItem);
        return values.join()?.toLowerCase().includes(lowerCaseST);
      });
    }
    return data;
  }, [data, searchText]);

  return (
    <Modal
      onCancel={onCancel}
      afterClose={afterClose}
      open={open}
      width="80%"
      style={{
        top: 150,
      }}
      bodyStyle={{
        padding: 0,
        height: '400px',
        display: 'flex',
        flexDirection: 'column',
      }}
      forceRender
      destroyOnClose={true}
      maskClosable={false}
      footer={null}
    >
      <ModelHeader>
        <div>
          <span
            onClick={() => {
              onIsPendingChange(!isPending);
            }}
          >
            {isPending ? <PauseButton /> : <StartButton />}
          </span>

          {/* <Space.Compact
          style={{
            borderRadius: '2px',
            border: '1px solid #B3C8FF',
          }}
        >
          <div
            style={{
              background: '#F2F6FF',
              letterSpacing: '-0.14px',
              display: 'flex',
              alignItems: 'center',
              padding: '0 8px',
            }}
          >
            Find User by IP Address :
          </div>
          <AutoComplete
            open={openDropdown}
            placeholder=""
            showSearch
            value={value}
            onSelect={onSelect}
            onSearch={onSearch}
            onDropdownVisibleChange={(open) => setOpenDropdown(open)}
            options={options}
            style={{ width: 304 }}
          >
            <InputSearchStyled
              size="large"
              placeholder="please input ip address"
              enterButton={
                <Button
                  style={{
                    background: '#F2F6FF',
                    borderRadius: 0,
                    color: '#0046FF',
                    border: 'none',
                  }}
                  onClick={() => {
                    onClickSearchButton?.();
                    setOpenDropdown(false);
                  }}
                >
                  <SearchOutlined />
                </Button>
              }
              onPressEnter={onPressEnter}
              style={{ color: '#0046FF' }}
            />
          </AutoComplete>
        </Space.Compact> */}
          <span>
            Found {data?.length || 0} users with activity within the last 5 mins. {searchTypeText}
          </span>
          <Input
            suffix={<SearchOutlined />}
            placeholder="Search..."
            allowClear
            onChange={onSearch}
            value={searchText}
            onPressEnter={onSearch}
            style={{ width: '240px', borderRadius: '1px', marginLeft: '24px' }}
          />
        </div>
      </ModelHeader>
      <div style={{ overflow: 'scroll' }}>
        <TableWrapper
          loading={isLoading && (data || []).length === 0}
          size="small"
          pagination={false}
          dataSource={resData}
          columns={columns}
        />
      </div>
    </Modal>
  );
};

export default FindUserByIpModal;
